// Shown when the product being configured can never be completed because the
// shoot cannot offer something it requires — currently only the missing
// background catalogue case (selectBackgroundCatalogueMissing): a green-screen
// shoot with background slots on the product but no catalogue to pick from.
//
// Add to Cart stays blocked (selectInitialStepsComplete keeps requiring the
// background, by design), so without this the customer sees a dead button and no
// explanation. It is a studio misconfiguration rather than anything the customer
// can act on, hence the "contact customer service" wording and the Sentry report.
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';
import styled from 'styled-components';

import { selectBackgroundCatalogueMissing } from '../../../../../selectors';

const Message = styled.div`
  padding: 16px 12px;
  margin-bottom: 16px;
  border: 1px solid #c0392b;
  border-radius: 3px;
  text-align: center;
  font-size: 15px;
  color: #c0392b;
`;

export const MISCONFIGURED_MESSAGE =
  'Product not configured correctly for purchase. Please contact customer service if this issue persists.';

const ProductMisconfiguredMessage: React.FC = () => {
  const catalogueMissing = useSelector(selectBackgroundCatalogueMissing);
  const product = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.productConfig.currentProduct,
  );
  const shootId = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.shoot?.shoot?.event_id || state.shoot?.shoot?.id,
  );

  // Keyed on the product/shoot pair so browsing between affected products
  // reports each one once, rather than on every render.
  useEffect(() => {
    if (!catalogueMissing) return;

    Sentry.captureException(
      new Error(
        `Product not configurable: product ${product?.id} ("${product?.name}") ` +
          `requires ${product?.background_options_count} background(s) but ` +
          `shoot ${shootId} is green screen with an empty background catalogue`,
      ),
    );
  }, [catalogueMissing, product?.id, shootId]);

  if (!catalogueMissing) return null;

  return <Message role="alert">{MISCONFIGURED_MESSAGE}</Message>;
};

export default ProductMisconfiguredMessage;
